<template>

  <footer class="mt-24 overflow-hidden border-t border-[#d6bca4] bg-[#9f1715] text-[#f8f1e7]">
    <div class="border-b border-[#bb4b3d] px-4 py-12 md:px-8">

      <div class="max-w-[1700px] mx-auto">
        <h2 class="font-['Anton'] leading-[0.85] text-[#f8f1e7]">
          <LogoComponent :theme="'white'" :customClass="'text-4xl md:text-6xl'" />
        </h2>

      </div>
    </div>

    <div 
    v-if="canDisplay('information')"
      class="max-w-[1700px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 px-4 py-16 md:px-8" 
    >
      <div>
        <h3 class="mb-5 text-sm font-black uppercase tracking-[0.2em]">
          {{ translate('about', 'ucfirst') }}
        </h3>

        <p class="max-w-sm text-sm leading-7 text-[#f5d9cc]">
          {{ translate('a modern cultural platform blending language learning with creativity, lifestyle, and inspiring stories', 'ucfirst') }}.
        </p>
      </div>

      <div>
        <h3 class="mb-5 text-sm font-black uppercase tracking-[0.2em]">
          {{ translate('navigation', 'ucfirst') }}
        </h3>

        <div class="flex flex-col gap-3">
          <p 
            v-for="(item, index) of navigationLinks" 
            :key="index" 
            :class="[
              'text-[#f5d9cc] hover:text-white cursor-pointer',
              item.auth == undefined || (item.auth == true && auth.isAuthenticated) ? '' : 'hidden'
            ]"
            @click="urlHandler.redirectAndScroll(item.link)"
          >
            {{ translate(item.label, 'ucfirst') }}
          </p>
        </div>
      </div>

      <div>
        <h3 class="mb-5 text-sm font-black uppercase tracking-[0.2em]">
          {{ translate('social', 'ucfirst') }}
        </h3>

        <div class="flex flex-col gap-3">
          <p 
            v-for="(item, index) of socialLinks" 
            :key="index" 
            class="text-[#f5d9cc] hover:text-white cursor-pointer" 
            @click="urlHandler.openExternal(item.link)"
          >
            {{ translate(item.label, 'ucfirst') }}
          </p>
        </div>

      </div>

      <div v-if="canDisplay('newsletter')">
        <h3 class="mb-5 text-sm font-black uppercase tracking-[0.2em]">
          {{ translate('newsletter', 'ucfirst') }}
        </h3>

        <p class="mb-5 text-sm leading-7 text-[#f5d9cc]">
          {{ translate('Receive weekly articles, trends and inspiration.') }}
        </p>

        <div class="flex overflow-hidden rounded-full border border-[#d68c7d] bg-[#b71e1b]">

          <input type="text" :placeholder="translate('your email', 'ucfirst')" class="h-14 flex-1 bg-transparent px-5 text-sm text-white placeholder:text-[#efc9bb] outline-none" />

          <button class="px-6 font-bold uppercase tracking-wide text-white hover:bg-[#ea6b1d] transition-all">
            {{ translate('join') }}
          </button>

        </div>
      </div>
    </div>

    <div class="border-t border-[#bb4b3d]">
      <div class="max-w-[1700px] mx-auto flex flex-col gap-5 px-4 py-6 text-sm md:flex-row md:items-center md:justify-between md:px-8 flex-wrap">

        <p class="text-[#f1d4c6]">
          <CompanyLabelComponent :textColorClass="'text-silver'" :textColorHighlight="'text-white'" />
        </p>

        <div class="flex flex-wrap items-center gap-6">

          <p class="text-[#f1d4c6] hover:text-white cursor-pointer" @click="urlHandler.redirectAndScroll('/privacy-policy')">
            {{ translate('privacy policy', 'capitalize') }}
          </p>

          <p class="text-[#f1d4c6] hover:text-white cursor-pointer" @click="urlHandler.redirectAndScroll('/terms-policy')">
            {{ translate('terms', 'ucfirst') }}
          </p>

          <button 
            @click="context.openLanguageModal" 
            class=" flex items-center gap-2 rounded-full border border-[#d68c7d] px-4 py-2 
              text-[#f8f1e7] hover:bg-[#ea6b1d] hover:border-[#ea6b1d] transition-all"
            >
            <i class="pi pi-globe"></i>

            {{ context.getLangObject.getName(true, true) }}
          </button>
        </div>
      </div>

      <p class="text-xs text-[#f5d9cc] mx-auto my-5 pb-5 text-center">
        {{ translate('developed with passion by', 'ucfirst') }} 
        <span class="font-bold text-white hover:text-[#ea6b1d] cursor-pointer transition-colors" @click="urlHandler.openExternal(context.appCreatorUrl)">
          {{ context.appCreator }}
        </span>
      </p>
    </div>

    <ChangeLanguageComponent :visible="context.isLanguageModalOpen" @closed="context.closeLanguageModal" />
  </footer>
</template>

<script setup>
import { translate } from '@/composables/translate';
import { useContextStore } from '@/stores/context';
import CompanyLabelComponent from '../misc/CompanyLabelComponent.vue';
import LogoComponent from '../LogoComponent.vue';
import ChangeLanguageComponent from '../Utils/ChangeLanguageComponent.vue';
import { ref, defineProps } from 'vue';
import BlogPostService from '@/services/BlogPostService';
import { useAuthStore } from '@/stores/auth.js';
import urlHandler from '@/composables/urlHandler.js';

const context = useContextStore();
const auth = useAuthStore();

const socialLinks = ref([
  { link: 'https://www.instagram.com/teachermaestravictoria/', label: 'instagram' }
]);

const navigationLinks = ref([
  { link: '/', label: 'home' },
  { link: '/blog/home', label: 'articles' },
  { link: BlogPostService.getAboutBlogPostInfo(), label: 'about' },
  { link: '/dashboard/support/', label: 'support', auth: true },
]);

const props = defineProps({
    type: {
      type: String,
      default: 'full',
    }
});

const displayOptions = {
  SIMPLE: [],
  FULL: [
    'information'
  ]
}

/**
 * Checks if can display content bu Type
 */
function canDisplay(id = null) {
  if (id == null) {
    return false;
  }

  const type = props.type.toUpperCase();
  const options = displayOptions[type] != undefined ? displayOptions[type] : null;
  if (options == undefined) {
    return false;
  }

  return options.includes(id);
}

</script>